import { Howl, Howler } from 'howler'
import { playLegacySound, setLegacySoundsMuted, stopLegacyPlayback } from './legacySounds'

const STORAGE_KEY = 'liveAnnouncementAudioEnabled'
const SEEN_TTL = 30000
const STALE_ANNOUNCEMENT_MS = 15000
const RELEVANCE_RETRY_MS = 100
const RELEVANCE_RETRY_LIMIT = 20
const LOT_BOUND_TYPES = [
    'lot_announce',
    'lot_announce_recall',
    'lot_announce_back_to_auction',
    'lot_idle',
    'lot_warn_once',
    'lot_warn_twice',
    'lot_warn_fairwarningclose',
    'lot_sell',
    'lot_pass',
    'lot_pass_reserve',
]

class AnnouncementAudioManager {
    constructor() {
        this.store = null
        this.currentHowl = null
        this.currentLegacyPlayback = null
        this.currentOverlayFxPlayback = null
        this.relevanceRetries = {}
        this.relevanceRetryTimers = {}
    }

    setStore(store) {
        this.store = store
    }

    initialize() {
        if (!this.store) {
            return
        }

        const enabled = this.getStoredPreference()

        this.store.commit('SET_AUDIO_ENABLED', enabled)
        this.store.commit('SET_AUDIO_BLOCKED', false)
        setLegacySoundsMuted(!enabled)

        if (enabled) {
            this.enable({ persist: false })
        }

        this.syncAuctionMode()
    }

    async enable(options = {}) {
        if (!this.store) {
            return false
        }

        const persist = options.persist !== false

        this.store.commit('SET_AUDIO_ENABLED', true)
        this.store.commit('SET_AUDIO_BLOCKED', false)
        setLegacySoundsMuted(false)

        if (persist) {
            this.persistPreference(true)
        }

        try {
            if (Howler.ctx && Howler.ctx.state === 'suspended') {
                await Howler.ctx.resume()
            }

            if (Howler.ctx && Howler.ctx.state === 'suspended') {
                throw new Error('Audio context is suspended')
            }

            this.processQueue()

            return true
        } catch (error) {
            this.store.commit('SET_AUDIO_BLOCKED', true)

            return false
        }
    }

    disable(options = {}) {
        if (!this.store) {
            return
        }

        const persist = options.persist !== false

        this.stopCurrent()
        setLegacySoundsMuted(true)
        this.store.commit('SET_AUDIO_ENABLED', false)
        this.store.commit('SET_AUDIO_BLOCKED', false)

        if (persist) {
            this.persistPreference(false)
        }
    }

    syncAuctionMode() {
        if (!this.supportsTimedTts()) {
            this.clearPlaybackState()
        }
    }

    enqueue(payload) {
        if (!this.store) {
            return
        }

        if (!this.supportsTimedTts()) {
            this.logDrop(payload, 'auction-mode-not-supported')
            this.clearPlaybackState()

            return
        }

        const announcement = this.normalize(payload)

        if (!announcement) {
            return
        }

        this.store.commit('PRUNE_AUDIO_SEEN_KEYS', Date.now() - SEEN_TTL)

        const staleReason = this.getStaleReason(announcement)

        if (staleReason) {
            this.clearRelevanceRetry(announcement)
            this.logDrop(announcement, staleReason)

            return
        }

        if (announcement.status === 'ready' && this.applyReadyAnnouncement(announcement)) {
            this.processQueue()

            return
        }

        const relevanceReason = this.getRelevanceReason(announcement)

        if (relevanceReason) {
            if (this.deferForRelevance(announcement, relevanceReason)) {
                return
            }

            this.clearRelevanceRetry(announcement)
            this.logDrop(announcement, relevanceReason)

            return
        }

        if (this.isDuplicate(announcement)) {
            this.clearRelevanceRetry(announcement)

            return
        }

        this.clearRelevanceRetry(announcement)

        this.markSeen(announcement)

        if (announcement.queueStrategy === 'flush_pending') {
            this.store.commit('CLEAR_AUDIO_QUEUE')
        } else if (announcement.queueStrategy === 'replace_pending' && announcement.queueGroup) {
            this.store.commit('REMOVE_AUDIO_ANNOUNCEMENTS_BY_GROUP', announcement.queueGroup)
        }

        this.store.commit('ENQUEUE_AUDIO_ANNOUNCEMENT', announcement)

        this.processQueue()
    }

    processQueue() {
        if (!this.store) {
            return
        }

        if (!this.supportsTimedTts()) {
            this.clearPlaybackState()

            return
        }

        if (!this.store.state.audioEnabled || this.store.state.activeAnnouncement) {
            return
        }

        const next = this.store.state.audioQueue[0]

        if (!next) {
            return
        }

        this.store.commit('REMOVE_AUDIO_ANNOUNCEMENT', next.id)

        const staleReason = this.getStaleReason(next)

        if (staleReason) {
            this.logDrop(next, staleReason)
            this.processQueue()

            return
        }

        const relevanceReason = this.getRelevanceReason(next)

        if (relevanceReason) {
            this.logDrop(next, relevanceReason)
            this.processQueue()

            return
        }

        this.store.commit('SET_ACTIVE_ANNOUNCEMENT', next)
        this.logPlayback(next)

        if (next.audioUrl) {
            this.playOverlayFxIfNeeded(next)
            this.playRemote(next)

            return
        }

        this.playFallback(next)
    }

    playRemote(announcement) {
        this.currentHowl = new Howl({
            src: [announcement.audioUrl],
            html5: true,
            format: ['mp3'],
            onend: () => this.finishCurrent(),
            onloaderror: (id, error) => this.handleRemoteError(announcement, error),
            onplayerror: (id, error) => this.handleRemoteError(announcement, error),
        })

        this.currentHowl.play()
    }

    playFallback(announcement) {
        if (!announcement.fallbackSound) {
            console.warn('[announcement-audio] no-fallback-sound', announcement)
            this.finishCurrent()

            return
        }

        this.currentLegacyPlayback = playLegacySound(announcement.fallbackSound)

        if (!this.currentLegacyPlayback) {
            console.warn('[announcement-audio] fallback-missing', {
                fallbackSound: announcement.fallbackSound,
                announcement,
            })
            this.finishCurrent()

            return
        }

        console.log('[announcement-audio] fallback-start', {
            fallbackSound: announcement.fallbackSound,
            state: this.currentLegacyPlayback.sound.state(),
            announcement,
        })

        this.currentLegacyPlayback.sound.once('end', () => {
            console.log('[announcement-audio] fallback-end', {
                fallbackSound: announcement.fallbackSound,
                announcementId: announcement.id,
            })
            this.finishCurrent()
        }, this.currentLegacyPlayback.id)

        this.currentLegacyPlayback.sound.once('playerror', () => {
            console.warn('[announcement-audio] fallback-playerror', {
                fallbackSound: announcement.fallbackSound,
                announcement,
            })
            this.handleBlockedPlayback(announcement)
        }, this.currentLegacyPlayback.id)

        this.currentLegacyPlayback.sound.once('loaderror', (id, error) => {
            console.warn('[announcement-audio] fallback-loaderror', {
                fallbackSound: announcement.fallbackSound,
                error,
                announcement,
            })
            this.handleBlockedPlayback(announcement)
        }, this.currentLegacyPlayback.id)
    }

    handleRemoteError(announcement, error) {
        const message = String(error || '')

        console.warn('[announcement-audio] remote-error', {
            error: message,
            announcement,
        })

        if (message.toLowerCase().includes('not allowed') || message.toLowerCase().includes('suspend')) {
            this.handleBlockedPlayback(announcement)

            return
        }

        this.currentHowl = null

        if (announcement.fallbackSound) {
            this.playFallback({
                ...announcement,
                audioUrl: null,
            })

            return
        }

        this.finishCurrent()
    }

    handleBlockedPlayback(announcement) {
        console.warn('[announcement-audio] blocked', announcement)
        this.stopCurrent()
        this.store.commit('SET_AUDIO_BLOCKED', true)
        this.store.commit('PREPEND_AUDIO_ANNOUNCEMENT', announcement)
    }

    finishCurrent() {
        this.stopCurrent()
        this.processQueue()
    }

    stopCurrent() {
        if (this.currentHowl) {
            this.currentHowl.stop()
            this.currentHowl.unload()
            this.currentHowl = null
        }

        if (this.currentLegacyPlayback) {
            stopLegacyPlayback(this.currentLegacyPlayback)
            this.currentLegacyPlayback = null
        }

        if (this.currentOverlayFxPlayback) {
            stopLegacyPlayback(this.currentOverlayFxPlayback)
            this.currentOverlayFxPlayback = null
        }

        if (this.store) {
            this.store.commit('SET_ACTIVE_ANNOUNCEMENT', null)
        }
    }

    playOverlayFxIfNeeded(announcement) {
        if (!this.shouldPlayOverlayFx(announcement)) {
            return
        }

        const fxPlayback = playLegacySound(announcement.fallbackSound || 'lotopen')

        if (!fxPlayback) {
            console.warn('[announcement-audio] overlay-fx-missing', announcement)

            return
        }

        this.currentOverlayFxPlayback = fxPlayback

        console.log('[announcement-audio] overlay-fx-start', {
            sound: announcement.fallbackSound || 'lotopen',
            announcement,
        })

        fxPlayback.sound.once('end', () => {
            if (this.currentOverlayFxPlayback && this.currentOverlayFxPlayback.id === fxPlayback.id) {
                this.currentOverlayFxPlayback = null
            }

            console.log('[announcement-audio] overlay-fx-end', {
                sound: announcement.fallbackSound || 'lotopen',
                announcementId: announcement.id,
            })
        }, fxPlayback.id)
    }

    normalize(payload) {
        if (!payload || !payload.id) {
            return null
        }

        return {
            id: payload.id,
            type: payload.type || 'announcement',
            text: payload.text || '',
            audioUrl: payload.audio_url || payload.audioUrl || null,
            priority: Number(payload.priority || 0),
            interrupt: Boolean(payload.interrupt),
            fallbackSound: payload.fallback_sound || payload.fallbackSound || null,
            queueStrategy: payload.queue_strategy || payload.queueStrategy || 'append',
            queueGroup: payload.queue_group || payload.queueGroup || 'default',
            dedupeKey: payload.dedupe_key || payload.dedupeKey || payload.id,
            createdAt: payload.created_at || payload.createdAt || new Date().toISOString(),
            lotId: payload.lot_id || payload.lotId || null,
            lotNumber: payload.lot_number || payload.lotNumber || null,
            status: payload.status || 'fallback',
            meta: payload.meta || {},
        }
    }

    deferForRelevance(announcement, reason) {
        const key = announcement.dedupeKey || announcement.id
        const retryCount = this.relevanceRetries[key] || 0

        if (retryCount >= RELEVANCE_RETRY_LIMIT || this.isStale(announcement)) {
            this.clearRelevanceRetry(announcement)

            return false
        }

        this.relevanceRetries[key] = retryCount + 1

        if (this.relevanceRetryTimers[key]) {
            window.clearTimeout(this.relevanceRetryTimers[key])
        }

        console.log('[announcement-audio] relevance-retry', {
            reason,
            attempt: this.relevanceRetries[key],
            announcement,
        })

        this.relevanceRetryTimers[key] = window.setTimeout(() => {
            delete this.relevanceRetryTimers[key]
            this.enqueue(announcement)
        }, RELEVANCE_RETRY_MS)

        return true
    }

    clearRelevanceRetry(announcement) {
        if (!announcement) {
            return
        }

        const key = announcement.dedupeKey || announcement.id

        if (this.relevanceRetryTimers[key]) {
            window.clearTimeout(this.relevanceRetryTimers[key])
            delete this.relevanceRetryTimers[key]
        }

        delete this.relevanceRetries[key]
    }

    clearAllRelevanceRetries() {
        Object.keys(this.relevanceRetryTimers).forEach(key => {
            window.clearTimeout(this.relevanceRetryTimers[key])
        })

        this.relevanceRetryTimers = {}
        this.relevanceRetries = {}
    }

    markSeen(announcement) {
        this.store.commit('MARK_AUDIO_ANNOUNCEMENT_SEEN', {
            key: announcement.dedupeKey,
            at: Date.now(),
            status: announcement.status,
            hasAudioUrl: Boolean(announcement.audioUrl),
        })
    }

    isDuplicate(announcement) {
        const seen = this.store.state.seenAnnouncementKeys[announcement.dedupeKey]

        if (!seen) {
            return false
        }

        if (this.isReadyAudioUpgrade(announcement, seen)) {
            return false
        }

        return true
    }

    isReadyAudioUpgrade(announcement, seen) {
        return announcement.status === 'ready'
            && Boolean(announcement.audioUrl)
            && !this.seenHasAudioUrl(seen)
    }

    seenHasAudioUrl(seen) {
        if (seen && typeof seen === 'object') {
            return Boolean(seen.hasAudioUrl)
        }

        return true
    }

    shouldPlayOverlayFx(announcement) {
        return Boolean(announcement.fallbackSound)
    }

    applyReadyAnnouncement(announcement) {
        const queuedAnnouncement = this.store.state.audioQueue.find(item => item.dedupeKey === announcement.dedupeKey)

        if (queuedAnnouncement) {
            this.clearRelevanceRetry(announcement)
            this.markSeen(announcement)
            this.store.commit('PROMOTE_AUDIO_ANNOUNCEMENT', announcement)
            console.log('[announcement-audio] ready-upgrade-queued', announcement)

            return true
        }

        const activeAnnouncement = this.store.state.activeAnnouncement

        if (activeAnnouncement && activeAnnouncement.dedupeKey === announcement.dedupeKey) {
            this.clearRelevanceRetry(announcement)

            if (announcement.audioUrl && !activeAnnouncement.audioUrl) {
                const upgradedAnnouncement = {
                    ...activeAnnouncement,
                    ...announcement,
                    fallbackSound: null,
                }

                this.markSeen(upgradedAnnouncement)
                this.store.commit('PREPEND_AUDIO_ANNOUNCEMENT', upgradedAnnouncement)
                console.log('[announcement-audio] ready-queue-after-active-fallback', upgradedAnnouncement)

                return true
            }

            this.markSeen(announcement)
            this.store.commit('SET_ACTIVE_ANNOUNCEMENT', {
                ...activeAnnouncement,
                ...announcement,
            })
            console.log('[announcement-audio] ready-upgrade-active', announcement)

            return true
        }

        if (this.isDuplicate(announcement)) {
            this.clearRelevanceRetry(announcement)
            console.log('[announcement-audio] ready-drop-seen', announcement)

            return true
        }

        return false
    }

    isStale(announcement) {
        return Boolean(this.getStaleReason(announcement))
    }

    getStaleReason(announcement) {
        const createdAt = new Date(announcement.createdAt).getTime()

        if (!createdAt) {
            return 'invalid-created-at'
        }

        const maxAgeMs = this.getMaxAgeMs(announcement)

        return Date.now() - createdAt > maxAgeMs
            ? `stale>${maxAgeMs}ms`
            : null
    }

    getRelevanceReason(announcement) {
        const currentLot = this.getCurrentLot()
        const expectedLotId = this.getExpectedLotId(announcement)
        const expectedLotNumber = this.getExpectedLotNumber(announcement)
        const expectedAsk = this.getExpectedAsk(announcement)
        const expectedCurrentPrice = this.getExpectedCurrentPrice(announcement)

        if ((this.requiresCurrentLot(announcement) || expectedLotId !== null || expectedLotNumber !== null || expectedAsk !== null || expectedCurrentPrice !== null) && !currentLot) {
            return 'missing-current-lot'
        }

        if (expectedLotId !== null && this.toNumber(currentLot.id) !== expectedLotId) {
            return `lot-id-mismatch:${expectedLotId}!=${this.toNumber(currentLot.id)}`
        }

        if (expectedLotNumber !== null && this.toNumber(currentLot.no) !== expectedLotNumber) {
            return `lot-number-mismatch:${expectedLotNumber}!=${this.toNumber(currentLot.no)}`
        }

        if (expectedAsk !== null && this.toNumber(currentLot.k) !== expectedAsk) {
            return `ask-mismatch:${expectedAsk}!=${this.toNumber(currentLot.k)}`
        }

        if (expectedCurrentPrice !== null && this.toNumber(currentLot.p) !== expectedCurrentPrice) {
            return `current-price-mismatch:${expectedCurrentPrice}!=${this.toNumber(currentLot.p)}`
        }

        return null
    }

    getMaxAgeMs(announcement) {
        const rawValue = this.firstDefined(
            announcement.meta.maxAgeMs,
            announcement.meta.max_age_ms,
        )

        const numericValue = this.toNumber(rawValue)

        if (numericValue !== null && numericValue > 0) {
            return numericValue
        }

        if (this.getExpectedAsk(announcement) !== null) {
            return 2000
        }

        return STALE_ANNOUNCEMENT_MS
    }

    getCurrentLot() {
        if (!this.store || !this.store.getters) {
            return null
        }

        return this.store.getters.getLot || null
    }

    getCurrentAuction() {
        if (!this.store || !this.store.getters) {
            return null
        }

        return this.store.getters.getAuction || null
    }

    supportsTimedTts() {
        const auction = this.getCurrentAuction()

        if (!auction) {
            return false
        }

        const mode = this.toNumber(this.firstDefined(
            auction.md,
            auction.mode,
        ))

        return mode === 4 || mode === 5
    }

    clearPlaybackState() {
        this.clearAllRelevanceRetries()
        this.stopCurrent()

        if (this.store) {
            this.store.commit('CLEAR_AUDIO_QUEUE')
        }
    }

    requiresCurrentLot(announcement) {
        return LOT_BOUND_TYPES.includes(announcement.type)
    }

    getExpectedLotId(announcement) {
        return this.toNumber(this.firstDefined(
            announcement.meta.expectedLotId,
            announcement.meta.expected_lot_id,
            announcement.lotId,
        ))
    }

    getExpectedLotNumber(announcement) {
        return this.toNumber(this.firstDefined(
            announcement.meta.expectedLotNumber,
            announcement.meta.expected_lot_number,
            announcement.lotNumber,
        ))
    }

    getExpectedAsk(announcement) {
        return this.toNumber(this.firstDefined(
            announcement.meta.expectedAsk,
            announcement.meta.expected_ask,
        ))
    }

    getExpectedCurrentPrice(announcement) {
        return this.toNumber(this.firstDefined(
            announcement.meta.expectedCurrentPrice,
            announcement.meta.expected_current_price,
        ))
    }

    firstDefined(...values) {
        for (let index = 0; index < values.length; index += 1) {
            if (values[index] !== undefined && values[index] !== null && values[index] !== '') {
                return values[index]
            }
        }

        return null
    }

    toNumber(value) {
        if (value === undefined || value === null || value === '') {
            return null
        }

        const numericValue = Number(value)

        return Number.isFinite(numericValue) ? numericValue : null
    }

    logDrop(announcement, reason) {
        console.log('[announcement-audio] drop', {
            reason,
            announcement,
            currentAuction: this.getCurrentAuction(),
            currentLot: this.getCurrentLot(),
        })
    }

    logPlayback(announcement) {
        console.log('[announcement-audio] play', announcement)
    }

    getStoredPreference() {
        const explicitPreference = window.localStorage.getItem(STORAGE_KEY)

        if (explicitPreference !== null) {
            return explicitPreference === 'true'
        }

        return !(/true/).test(window.localStorage.getItem('soundsAreMuted'))
    }

    persistPreference(enabled) {
        window.localStorage.setItem(STORAGE_KEY, enabled ? 'true' : 'false')
        window.localStorage.setItem('soundsAreMuted', enabled ? 'false' : 'true')
    }
}

export default new AnnouncementAudioManager()
